import { rem } from 'polished'
import { useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import HeroImage from 'content/components/HeroImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelSearchForm from 'components/Search/Hotels/HotelSearchForm'
import GlobalSearchLoyaltyBurnToggle from 'components/Search/GlobalSearch/GlobalSearchLoyaltyBurnToggle'
import config from 'constants/config'
import { convertDatesToString, encodeSearchParams, isSearchEnabled } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import zIndex from 'styles/tools/z-index'
import HotelsTabContent from 'components/SearchV2/Components/Mobile/TabContents/HotelsTabContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { useLocation } from 'react-router-dom'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

const HeroContainer = styled(HeroImage)`
  min-height: unset;
  padding: ${rem(40)} 0;

  ${mediaQueryDown.mobile} {
    &.expanded-mobile-search {
      padding-bottom: ${rem(140)};
    }

    &.expanded-mobile-search.burn-enabled {
      padding-bottom: ${rem(156)};
    }

    &.expanded-mobile-search-compact {
      padding-bottom: ${rem(32)};
    }
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
  }
`

const SolidHeroContainer = styled.div`
  min-height: unset;
  padding: ${rem(40)} 0;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  ${mediaQueryDown.mobile} {
    &.expanded-mobile-search {
      padding-bottom: ${rem(140)};
    }

    &.expanded-mobile-search.burn-enabled {
      padding-bottom: ${rem(156)};
    }

    &.expanded-mobile-search-compact {
      padding-bottom: ${rem(32)};
    }
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
  }
`

const HotelSearchContainerPaneContent = styled.div<{ $useBrandBorder?: boolean; $useDropShadow?: boolean }>`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${props => (props.$useBrandBorder ? `1px solid ${props.theme.palette.brand.primary.normal}` : 'none')};
  box-shadow: ${props => (props.$useDropShadow ? `0 ${rem(8)} ${rem(24)} rgba(0, 0, 0, 0.12)` : 'none')};

  &.mobile-expanded {
    margin-top: ${rem(16)};
    margin-bottom: ${rem(16)};
  }
`

const HotelSearchBarFloating = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateY(50%);
  width: 100%;
  z-index: ${zIndex.floatingPanel};

  &.floating-search-bar {
    position: relative;
    transform: unset;
    margin-top: ${rem(32)};
  }
`

interface Props {
  floatingSearchBar?: boolean
  useInlineSearchBar?: boolean
  useCompactMobileExpandedSpacing?: boolean
  heroTitle?: React.ReactNode
  heroSubtitle?: React.ReactNode
  useDarkBackground?: boolean
  useBrandBorder?: boolean
  searchBasePath?: string
  searchBaseMapPath?: string
  useDropShadow?: boolean
  heroImageId?: string
  burnEnabled?: boolean
}

function HotelPageSearchHero({
  floatingSearchBar,
  useInlineSearchBar = false,
  useCompactMobileExpandedSpacing = false,
  heroTitle,
  heroSubtitle,
  useDarkBackground = false,
  useBrandBorder = false,
  searchBasePath = '/search',
  searchBaseMapPath,
  useDropShadow = false,
  heroImageId,
  burnEnabled,
  isSearchEnabled,
  filters,
}: Props & MappedStateProps) {
  const intl = useIntl()

  const defaultHeroTitle = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'CUktKBLdZ4p5pZxeUUdP',
          defaultMessage: '<italic>Hotels & Resorts</italic> to dream about',
          description: 'Default hero title for hotel search page with italic styling',
        },
        {
          italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      ),
    [intl],
  )

  const defaultHeroSubtitle = intl.formatMessage({
    id: 'OoXWNzskPpTHA_ke8jCs',
    defaultMessage: 'Choose from our curated hotels & resorts',
    description: 'Default hero subtitle for hotel search page in the search interface',
  })

  const searchButtonText = intl.formatMessage({
    id: 'VdCRgqOf7EmkdxhT3Wa-',
    defaultMessage: 'Search',
    description: 'Search button text on hotel search page on the hotel or room booking page',
  })

  const resolvedHeroTitle = heroTitle ?? defaultHeroTitle
  const resolvedHeroSubtitle = heroSubtitle ?? defaultHeroSubtitle

  const globalSearchState = useContext(GlobalSearchStateContext)
  const { search } = useLocation()
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const shouldInlineSearchBar = useInlineSearchBar || !!floatingSearchBar
  const resolvedSearchMapPath = searchBaseMapPath ?? `${searchBasePath}/map`
  const expandedMobileSearchClassName = clsx(
    useCompactMobileExpandedSpacing ? 'expanded-mobile-search-compact' : 'expanded-mobile-search',
    { 'burn-enabled': burnEnabled },
  )

  const onSearch = () => {
    if (formRef.current?.reportValidity()) {
      const {
        searchItem,
        occupancies,
        checkinDate,
        checkoutDate,
        flexibleNights,
        isFlexibleDateSelected,
        flexibleMonths,
        userSelectedFlexibleMonths,
      } = globalSearchState

      const dates = convertDatesToString(checkinDate, checkoutDate)
      const queryParams = encodeSearchParams({
        urlSearch: search,
        searchItem,
        filters,
        dates,
        rooms: occupancies,
        isFlexibleWithDate: isFlexibleDateSelected,
        flexibleNights,
        flexibleMonths,
        userSelectedFlexibleMonths,
      }).toString()

      dispatch(pushWithRegion(`${searchBasePath}?${queryParams}`))
    }
  }

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearch()
  }

  if (!isSearchEnabled) {
    return null
  }

  const renderSearchHeroContent = () => (
    <>
      <LayoutContainer relative>
        <Group direction="vertical" horizontalAlign="start" tabletHorizontalAlign="center" gap={8}>
          <Heading colour={useDarkBackground ? 'normal-contrast' : 'dark-contrast'} variant="heading2" className="hero-heading center">
            {resolvedHeroTitle}
          </Heading>
          <BodyText variant="large" colour={useDarkBackground ? 'normal-contrast' : 'dark-contrast'} className="center">
            {resolvedHeroSubtitle}
          </BodyText>
        </Group>
      </LayoutContainer>

      <HotelSearchBarFloating className={clsx({ 'floating-search-bar': shouldInlineSearchBar })}>
        <LayoutContainer>
          <CSSBreakpoint min="tablet">
            <HotelSearchContainerPaneContent $useBrandBorder={useBrandBorder} $useDropShadow={useDropShadow}>
              <VerticalSpacer gap={16}>
                <HotelSearchForm
                  submitOnApply
                  baseSearchPath={searchBasePath}
                  baseSearchMapPath={resolvedSearchMapPath}
                />
                <GlobalSearchLoyaltyBurnToggle compact />
              </VerticalSpacer>
            </HotelSearchContainerPaneContent>
          </CSSBreakpoint>

          <CSSBreakpoint max="mobile">
            <HotelSearchContainerPaneContent className="mobile-expanded">
              <VerticalSpacer as="form" onSubmit={onSearchSubmit} gap={12} ref={formRef}>
                <HotelsTabContent
                  isInPage
                  onSearch={onSearch}
                />
                <TextButton fit="mobile-full-width" kind="primary" type="submit" size="large">{searchButtonText}</TextButton>
                <GlobalSearchLoyaltyBurnToggle compact />
              </VerticalSpacer>
            </HotelSearchContainerPaneContent>
          </CSSBreakpoint>
        </LayoutContainer>
      </HotelSearchBarFloating>
    </>
  )

  if (useDarkBackground) {
    return (
      <SolidHeroContainer className={expandedMobileSearchClassName}>
        {renderSearchHeroContent()}
      </SolidHeroContainer>
    )
  }

  return (
    <HeroContainer
      className={expandedMobileSearchClassName}
      gravity="auto"
      aria-hidden="true"
      brightness={-25}
      id={heroImageId ?? config.searchFormSection.hotelsImageId}
      tabletAspectRatio="16:3"
    >
      {renderSearchHeroContent()}
    </HeroContainer>
  )
}

interface MappedStateProps {
  isSearchEnabled: boolean
  filters: App.OfferListFilters
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  isSearchEnabled: isSearchEnabled(),
  filters: getUrlOfferListFilters(state),
})

export default connect(mapStateToProps)(HotelPageSearchHero)
